<template>
  <div class="why-renants-page">
    <Navigation />
    
    <div class="main-content">
      <!-- Hero Section -->
      <section class="hero-section">
        <div class="hero-container">
          <h1 class="hero-title">Why Choose Renants?</h1>
          <p class="hero-subtitle">The Indian community-first marketplace built for locals, by locals</p>
        </div>
      </section>

      <!-- Key Features Section -->
      <section class="features-section">
        <div class="features-container">
          <h2 class="section-title">Why Renants Stands Out</h2>
          
          <div class="features-grid">
            <!-- Feature 1 -->
            <div class="feature-card">
              <div class="feature-icon">🇮🇳</div>
              <h3 class="feature-title">Community-First Focus</h3>
              <p class="feature-description">
                Built specifically for the Indian community with culturally relevant services and local trust networks. We understand your needs better.
              </p>
            </div>

            <!-- Feature 2 -->
            <div class="feature-card">
              <div class="feature-icon">⚡</div>
              <h3 class="feature-title">Simplified Posting Experience</h3>
              <p class="feature-description">
                Post your service or product in just 1-2 steps. No complicated forms or unnecessary fields. Quick, easy, and intuitive.
              </p>
            </div>

            <!-- Feature 3 -->
            <div class="feature-card">
              <div class="feature-icon">⭐</div>
              <h3 class="feature-title">Free Featured Listings</h3>
              <p class="feature-description">
                Get your listings featured for free as a starter. Stand out without paying premium fees upfront.
              </p>
            </div>

            <!-- Feature 4 -->
            <div class="feature-card">
              <div class="feature-icon">🔒</div>
              <h3 class="feature-title">Community Trust & Safety Built In</h3>
              <p class="feature-description">
                Strong verification badges, trust scores, and community feedback ensure a safer marketplace for everyone.
              </p>
            </div>

            <!-- Feature 5 -->
            <div class="feature-card">
              <div class="feature-icon">💬</div>
              <h3 class="feature-title">WhatsApp + Community Posting Flow</h3>
              <p class="feature-description">
                Post directly via WhatsApp or use our simple web interface. Multiple ways to connect with your community.
              </p>
            </div>

            <!-- Feature 6 -->
            <div class="feature-card">
              <div class="feature-icon">👁️</div>
              <h3 class="feature-title">Complete Transparency</h3>
              <p class="feature-description">
                Clear pricing, honest reviews, and transparent community standards. No hidden fees or surprise charges.
              </p>
            </div>
          </div>
        </div>
      </section>

      <!-- Comparison Section -->
      <section class="comparison-section">
        <div class="comparison-container">
          <h2 class="section-title">How We Compare</h2>
          <p class="comparison-subtitle">Renants vs Other Marketplaces</p>
          
          <div class="table-wrapper">
            <table class="comparison-table">
              <thead>
                <tr>
                  <th class="feature-col">Feature / Value</th>
                  <th class="renants-col">Renants.com</th>
                  <th class="competitor-col">other players</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="feature-name">Indian-Community Focus</td>
                  <td class="renants-value"><span class="badge badge-strong">✅ Strong</span></td>
                  <td class="competitor-value"><span class="badge badge-weak">❌ General</span></td>
                </tr>
                <tr>
                  <td class="feature-name">City-First Listings</td>
                  <td class="renants-value"><span class="badge badge-strong">✅ Yes</span></td>
                  <td class="competitor-value"><span class="badge badge-mixed">⚠️ Mixed</span></td>
                </tr>
                <tr>
                  <td class="feature-name">Easy Posting</td>
                  <td class="renants-value"><span class="badge badge-strong">✅ 1–2 steps</span></td>
                  <td class="competitor-value"><span class="badge badge-mixed">⚠️ More steps</span></td>
                </tr>
                <tr>
                  <td class="feature-name">WhatsApp Posting</td>
                  <td class="renants-value"><span class="badge badge-strong">✅ Yes</span></td>
                  <td class="competitor-value"><span class="badge badge-weak">❌ No</span></td>
                </tr>
                <tr>
                  <td class="feature-name">Local Events / Boards</td>
                  <td class="renants-value"><span class="badge badge-strong">✅ Yes</span></td>
                  <td class="competitor-value"><span class="badge badge-weak">❌ No</span></td>
                </tr>
                <tr>
                  <td class="feature-name">Culture-Specific Services</td>
                  <td class="renants-value"><span class="badge badge-strong">✅ Deep</span></td>
                  <td class="competitor-value"><span class="badge badge-mixed">⚠️ Broad</span></td>
                </tr>
                <tr>
                  <td class="feature-name">Trust Badges / Verification</td>
                  <td class="renants-value"><span class="badge badge-strong">✅ Strong</span></td>
                  <td class="competitor-value"><span class="badge badge-mixed">⚠️ Limited</span></td>
                </tr>
                <tr>
                  <td class="feature-name">Free Featured Starter</td>
                  <td class="renants-value"><span class="badge badge-strong">✅ Yes</span></td>
                  <td class="competitor-value"><span class="badge badge-mixed">⚠️ Paid Focus</span></td>
                </tr>
                <tr>
                  <td class="feature-name">Local SEO Targeting</td>
                  <td class="renants-value"><span class="stars">⭐⭐⭐⭐</span></td>
                  <td class="competitor-value"><span class="stars">⭐⭐⭐</span></td>
                </tr>
                <tr>
                  <td class="feature-name">Personalized Recommendations</td>
                  <td class="renants-value"><span class="badge badge-strong">✅ Future</span></td>
                  <td class="competitor-value"><span class="badge badge-mixed">⚠️ Not niche</span></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <!-- CTA Section -->
      <section class="cta-section">
        <div class="cta-container">
          <h2 class="cta-title">Ready to Get Started?</h2>
          <p class="cta-description">Join thousands of Indians already using Renants to connect with their local community.</p>
          <button class="cta-button" @click="openPostAd">Post Your First Ad Today</button>
        </div>
      </section>
    </div>

    <Footer />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import Navigation from './Navigation.vue';
import Footer from './footer.vue';
import { EventBus } from '../../services/EventBus.js';

export default defineComponent({
  name: 'WhyRenants',
  components: {
    Navigation,
    Footer
  },
  methods: {
    openPostAd() {
      // Ask the Navigation component (already on this page) to run its
      // "Post Free Ad" flow: opens the login modal if needed, else PostModal.
      EventBus.requestPostAd();
    }
  }
});
</script>

<style scoped>
.why-renants-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.main-content {
  flex: 1;
  width: 100%;
  background: #f9f9f9;
}

/* ===== HERO SECTION ===== */
.hero-section {
  background: linear-gradient(135deg, #3467d5 0%, #2651b1 100%);
  color: white;
  padding: 80px 32px;
  text-align: center;
}

.hero-container {
  max-width: 800px;
  margin: 0 auto;
}

.hero-title {
  font-size: 48px;
  font-weight: 700;
  margin: 0 0 16px 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
}

.hero-subtitle {
  font-size: 20px;
  font-weight: 400;
  margin: 0;
  opacity: 0.95;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
}

/* ===== FEATURES SECTION ===== */
.features-section {
  padding: 80px 32px;
  background: white;
}

.features-container {
  max-width: 1200px;
  margin: 0 auto;
}

.section-title {
  font-size: 40px;
  font-weight: 700;
  text-align: center;
  margin: 0 0 60px 0;
  color: #1a1a1a;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 32px;
}

.feature-card {
  padding: 32px;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  background: #f9f9f9;
  transition: all 0.3s ease;
  text-align: center;
}

.feature-card:hover {
  border-color: #3467d5;
  box-shadow: 0 8px 24px rgba(52, 103, 213, 0.1);
  transform: translateY(-4px);
}

.feature-icon {
  font-size: 48px;
  margin-bottom: 16px;
  display: block;
}

.feature-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 12px 0;
  color: #1a1a1a;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
}

.feature-description {
  font-size: 15px;
  color: #666;
  margin: 0;
  line-height: 1.6;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
}

/* ===== COMPARISON SECTION ===== */
.comparison-section {
  padding: 80px 32px;
  background: #f9f9f9;
}

.comparison-container {
  max-width: 1200px;
  margin: 0 auto;
}

.comparison-subtitle {
  font-size: 18px;
  text-align: center;
  color: #666;
  margin: 0 0 40px 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
}

.table-wrapper {
  overflow-x: auto;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.comparison-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
}

.comparison-table thead tr {
  background: linear-gradient(135deg, #3467d5 0%, #2651b1 100%);
  color: white;
}

.comparison-table th {
  padding: 20px 16px;
  text-align: left;
  font-weight: 600;
  font-size: 14px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
}

.comparison-table td {
  padding: 18px 16px;
  border-bottom: 1px solid #e0e0e0;
  font-size: 14px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
}

.comparison-table tbody tr:hover {
  background: #f5f5f5;
}

.feature-col {
  min-width: 200px;
}

.renants-col {
  min-width: 180px;
  text-align: center;
}

.competitor-col {
  min-width: 180px;
  text-align: center;
}

.feature-name {
  font-weight: 500;
  color: #1a1a1a;
}

.renants-value,
.competitor-value {
  text-align: center;
}

.badge {
  display: inline-block;
  padding: 6px 12px;
  border-radius: 6px;
  font-weight: 500;
  font-size: 13px;
}

.badge-strong {
  background: #e8f5e9;
  color: #2e7d32;
}

.badge-weak {
  background: #ffebee;
  color: #c62828;
}

.badge-mixed {
  background: #fff3e0;
  color: #e65100;
}

.stars {
  display: inline-block;
  font-size: 16px;
}

/* ===== CTA SECTION ===== */
.cta-section {
  padding: 80px 32px;
  background: linear-gradient(135deg, #3467d5 0%, #2651b1 100%);
  color: white;
  text-align: center;
}

.cta-container {
  max-width: 600px;
  margin: 0 auto;
}

.cta-title {
  font-size: 36px;
  font-weight: 700;
  margin: 0 0 16px 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
}

.cta-description {
  font-size: 16px;
  margin: 0 0 32px 0;
  opacity: 0.95;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
}

.cta-button {
  background: white;
  color: #3467d5;
  border: none;
  padding: 14px 32px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
}

.cta-button:hover {
  background: #f0f0f0;
  transform: scale(1.05);
}

/* ===== RESPONSIVE ===== */
@media (max-width: 768px) {
  .hero-section {
    padding: 60px 24px;
  }

  .hero-title {
    font-size: 32px;
  }

  .hero-subtitle {
    font-size: 16px;
  }

  .features-section,
  .comparison-section,
  .cta-section {
    padding: 60px 24px;
  }

  .section-title {
    font-size: 28px;
    margin-bottom: 40px;
  }

  .features-grid {
    gap: 24px;
  }

  .feature-card {
    padding: 24px;
  }

  .feature-icon {
    font-size: 40px;
  }

  .feature-title {
    font-size: 18px;
  }

  .comparison-table th,
  .comparison-table td {
    padding: 14px 12px;
    font-size: 12px;
  }

  .feature-col {
    min-width: 150px;
  }

  .renants-col,
  .competitor-col {
    min-width: 120px;
  }

  .cta-title {
    font-size: 28px;
  }

  .cta-description {
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .hero-section {
    padding: 40px 16px;
  }

  .hero-title {
    font-size: 24px;
  }

  .hero-subtitle {
    font-size: 14px;
  }

  .features-section,
  .comparison-section,
  .cta-section {
    padding: 40px 16px;
  }

  .section-title {
    font-size: 22px;
    margin-bottom: 30px;
  }

  .features-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .feature-card {
    padding: 20px;
  }

  .feature-icon {
    font-size: 36px;
    margin-bottom: 12px;
  }

  .feature-title {
    font-size: 16px;
  }

  .feature-description {
    font-size: 14px;
  }

  .comparison-subtitle {
    margin-bottom: 20px;
  }

  .comparison-table th,
  .comparison-table td {
    padding: 12px 8px;
    font-size: 11px;
  }

  .badge {
    padding: 4px 8px;
    font-size: 11px;
  }

  .cta-title {
    font-size: 22px;
  }

  .cta-description {
    font-size: 13px;
    margin-bottom: 24px;
  }

  .cta-button {
    padding: 12px 24px;
    font-size: 14px;
  }
}
</style>
